import type { JSX } from "react";
import {
  setSearchProducts,
  setSearchQuery,
} from "@/store/features/globalSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addSlash,
  debounce,
  getImageArray,
  numberWithCommas,
  replaceSlash,
} from "@/utils";
import { placeholderImage, routes } from "@/utils/constants";
import { getS3ImageUrl } from "@/utils/routes";
import { CloseIcon } from "@mantine/core";
import { upperFirst } from "@mantine/hooks";
import Image from "next/image";
import Link from "next/link";
import { type CompatRouter as NextRouter, useRouter } from "@/hooks/useCompatRouter";
import { CSSProperties, useEffect, useState } from "react";
import { ArrowSquareLeftOutlineIcon, SearchOutlineIcon } from "../Icons";
import classes from "./SearchBar.module.css";
import { getSuggestedProductsByElasticSearch } from "@/requests/product";
import ProductCardSearch from "../ProductCard/ProductCardSearch";
import Loader from "../Loader";

function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// component for suggested search
const SuggestedSearch = ({
  suggestedSearch,
  onSelect,
  onClearCall = () => {},
  loading = false,
  size = "lg",
}: {
  suggestedSearch?: SuggestedSearchResultV2;
  onSelect: (item: SuggestedSearchDataTypeV2, newTab?: boolean) => void;
  onClearCall?: () => void;
  loading?: boolean;
  size?: "sm" | "lg";
}) => {
  const classNames = [classes.suggestedSearch, classes[size]];

  if (suggestedSearch?.suggestion?.length === 0) classNames.push(classes.hide);

  const withImage: SuggestedSearchDataTypeV2[] = [];
  const withoutImage: SuggestedSearchDataTypeV2[] = [];

  for (const item of suggestedSearch?.suggestion || []) {
    if (!item.name) continue;

    if (item.datatype === "search" || !item.thumbnail) {
      withoutImage.push(item);
    } else {
      withImage.push(item);
    }
  }

  const suggestions: SuggestedSearchDataTypeV2[] =
    suggestedSearch?.suggestion || [];

  return (
    <div className={classNames.join(" ")}>
      {loading && (
        <div className="flex justify-center items-center py-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
        </div>
      )}

      <div className="flex justify-between">
        {suggestedSearch?.title && (
          <p className="text-xs font-semibold text-gray-400 mb-2">
            {suggestedSearch?.title}
          </p>
        )}

        {suggestedSearch?.title?.match(/history/i) && (
          <button
            className="text-xs font-semibold text-gray-400"
            //   onClick={() => {
            //     clearSearchHistory(userId).then(() => {
            //       onClearCall();
            //     });
            //   }}
          >
            Clear
          </button>
        )}
      </div>

      {suggestions.map((item, i) => {
        const stock = +item.stockQuantity;

        let elem: JSX.Element = (
          <div
            className={
              classes.product + (item.datatype === "search" ? " !py-1" : "")
            }
          >
            {!!item.thumbnail && (
              <Image
                src={
                  getS3ImageUrl(item.thumbnail.split(",")[0], "MEDIA") ||
                  placeholderImage
                }
                alt={item.name??'product image'}
                width={size === "lg" ? 40 : 30}
                height={size === "lg" ? 40 : 30}
                layout="fixed"
                className="rounded-sm"
                unoptimized={true}
              />
            )}

            <div>
              <p className={classes.name}>{upperFirst(item.name)}</p>
              {!!item.price && (
                <p className={`${classes.name} flex gap-2 items-center`}>
                  <>
                    <span> Tk. </span>
                    <span className="font-semibold">
                      {numberWithCommas(item.discounted_price || item.price)}
                    </span>
                  </>

                  {/* {item?.stockStatus !== "Coming Soon" ? (
                    <>
                      <span> Tk. </span>
                      <span className="font-semibold">
                        {numberWithCommas(item.discounted_price || item.price)}
                      </span>
                    </>
                  ) : null} */}

                  <span
                    className={`${
                      item?.stockStatus === "In Stock"
                        ? "bg-green-200 text-green-700"
                        : item?.stockStatus === "Coming Soon"
                          ? "bg-gray-200 text-gray-700"
                          : "bg-red-200 text-red-700"
                    } text-[10px] px-1 rounded font-medium`}
                  >
                    {item?.stockStatus}
                  </span>
                </p>
              )}
            </div>

            {/* {!!item.datatype && item.datatype !== 'search' && (
                <p className='text-xs text-gray-400'>
                  {upperFirst(item.datatype)}
                </p>
              )} */}
          </div>
        );

        return (
          <Link
            key={item.slug + "-" + i}
            href={item.slug ? routes.product + item.slug : "#"}
            className="flex gap-2 items-center"
          >
            <button
              className={classes.suggestedSearchItem}
              onClick={(e) => {
                e.preventDefault();
                onSelect(item);
              }}
            >
              {elem}
            </button>

            {/* <button
                className='pb-[10px]'
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(item, true);
                }}
              >
                <RightIcon width={14} height={14} className='-rotate-45 -translate-y-1/3' />
              </button> */}
          </Link>
        );
      })}
    </div>
  );
};

const useSuggestedSearch = ({
  searchText,
  flag = true,
  limit = 12,
}: {
  searchText: string;
  flag?: boolean;
  limit?: number;
}) => {
  const [suggestedSearch, setSuggestedSearch] =
    useState<SuggestedSearchResultV2>();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const abortController = new AbortController();

    if (flag && !!searchText && searchText.length >= 1) {
      setLoading(true);

      timeout = setTimeout(() => {
        getSuggestedProductsByElasticSearch({
          searchText: searchText,
          limit: limit,
          config: {
            signal: abortController.signal,
          },
        })
          .then((data) => {
            const suggestion: SuggestedSearchDataTypeV2[] = [];

            for (const item of data.data) {
              suggestion.push({
                name: item.name,
                slug: item.slug,
                thumbnail: item.thumbnail,
                price: item.price,
                discounted_price: item.discounted_price,
                stockQuantity: item.stockQuantity,
                datatype: "product",
                stockStatus: item?.stockStatus,
              });
            }

            setSuggestedSearch({
              title: "Products",
              suggestion: suggestion,
            });
          })
          .catch((error) => {
            // Handle errors including aborted requests
            if (error.name !== "AbortError" && error.name !== "CanceledError") {
              console.error("Search request failed:", error);
            }
            // Don't set suggested search on error/abort
          })
          .finally(() => {
            setLoading(false);
          });
      }, 10);
    } else {
      // setSuggestedSearchs([]);
      timeout = setTimeout(() => {
        setSuggestedSearch(undefined);
      }, 100);
    }

    return () => {
      clearTimeout(timeout);

      try {
        abortController?.signal?.throwIfAborted();

        if (abortController?.signal?.aborted === false)
          abortController?.abort();
      } catch (error) {}
    };
  }, [searchText, flag, limit]);

  // const searchProducts = useAppSelector((state) => state.global.searchProducts);
  // const loading = useAppSelector((state) => state.global.searchLoading);

  // const suggestedSearch: SuggestedSearchResponse = {
  //   title: "Products",
  //   suggestion: searchProducts.map((item) => {
  //     return {
  //       id: +(item.p_id as any),
  //       name: item.name || "",
  //       image: getImageArray(item.thumbnail)[0] || "",
  //       datatype: "product",
  //       slug: item.slug,
  //       price: item.discounted_price || item.price,
  //       stockQuantity: item.stockQuantity,
  //     };
  //   }),
  // };

  return {
    suggestedSearch,
    loading,
  };
};

const routeChange = (router: NextRouter, url: string) => {
  // router.push(url, undefined, { shallow: true });
  if (router.pathname === "/search/[text]") {
    window.history.pushState({}, "", url);
  } else {
    router.push("/search/[text]", url, { shallow: true }).catch((error) => {
      // Handle route change errors (typically from aborted navigation)
      if (error?.cancelled) {
        // Route change was cancelled, this is expected behavior
        return;
      }
      console.error("Route change failed:", error);
    });
  }
};

const routeChangeDebounce = debounce(routeChange, 1);

interface Props {
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

let routeChangeStart = false;
let routeUrl = "";

// const SearchBar = ({ className, style }: Props) => {
//   const router = useRouter();
//   const dispatch = useAppDispatch();

//   const classNames = [classes.SearchBar, className || "", classes.lg];

//   const [searchText, setSearchText] = useState<string>(
//     router.query?.text?.toString() || ""
//   );

//   // Debounced search value
//   const debouncedSearchText = useDebounce(searchText, 800);

//   const [dropdownActive, setDropdownActive] = useState<boolean>(false);

//   const { suggestedSearch, loading: suggestedSearchLoading } =
//     useSuggestedSearch({
//       searchText: addSlash(debouncedSearchText),
//       flag: dropdownActive,
//     });

//   // Always keep suggestions type-safe
//   const suggestions: any[] = suggestedSearch?.suggestion ?? [];

//   // Dropdown should open only when API is loading or data exists
//   const shouldShowDropdown =
//     dropdownActive && debouncedSearchText.trim().length > 0;

//   // dropdown active class
//   if (shouldShowDropdown) {
//     classNames.push(classes.dropdownActive);
//   }

//   const submitHandler = (e: React.FormEvent) => {
//     e.preventDefault();
//     setDropdownActive(false);
//     dispatch(setSearchProducts([]));
//     dispatch(setSearchQuery(searchText));

//     router.push(routes.search + replaceSlash(searchText)).catch(() => {});
//   };

//   return (
//     <div className="relative w-full" style={style}>
//       <form
//         className={classNames.join(" ")}
//         onSubmit={submitHandler}
//         style={{ position: "relative", zIndex: 51 }}
//       >
//         <input
//           type="text"
//           className={classes.input}
//           value={searchText}
//           onFocus={() => setDropdownActive(true)}
//           onChange={(e) => {
//             setSearchText(e.target.value);
//             setDropdownActive(true);
//           }}
//           placeholder="Search products"
//         />

//         <button type="submit" className={classes.btn}>
//           <SearchOutlineIcon
//             className="!stroke-primary"
//             width={20}
//             height={20}
//           />
//         </button>
//       </form>

//       {/* Search dropdown */}
//       {shouldShowDropdown && (
//         <>
//           {/* Overlay */}
//           <div
//             onClick={() => setDropdownActive(false)}
//             style={{
//               position: "fixed",
//               inset: 0,
//               zIndex: 40,
//               backgroundColor: "rgb(0 0 0 / 30%)",
//               backdropFilter: "blur(2px)",
//               WebkitBackdropFilter: "blur(2px)",
//             }}
//           />

//           <div
//             className={classes.searchDropdown}
//             style={{
//               position: "absolute",
//               top: "100%",
//               left: "72%",
//               transform: "translateX(-50%)",
//               width: "60vw",
//               minWidth: "300px",
//               backgroundColor: "white",
//               zIndex: 50,
//               boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
//               padding: "30px 30px",
//               marginTop: "15px",
//               borderRadius: "12px",
//               maxHeight: "400px",
//               overflowY: "auto",
//               minHeight: "60vh",
//             }}
//           >
//             <div>
//               {/* Loading state */}
//               {suggestedSearchLoading && (
//                 <div className="flex justify-center items-center h-full">
//                   <Loader size={100} />
//                 </div>
//               )}

//               {!suggestedSearchLoading && suggestions.length === 0 && (
//                 <div className="flex flex-col justify-center items-center h-full py-16 text-center">
//                   {/* Icon */}
//                   <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
//                     <SearchOutlineIcon
//                       width={28}
//                       height={28}
//                       className="text-gray-400"
//                     />
//                   </div>

//                   {/* Title */}
//                   <h2 className="text-lg font-semibold text-gray-800">
//                     No products found
//                   </h2>

//                   {/* Description */}
//                   <p className="mt-2 max-w-xs text-sm text-gray-500">
//                     We couldn’t find any products matching
//                     <span className="font-medium text-gray-700">
//                       {" "}
//                       “{debouncedSearchText}”
//                     </span>
//                     . Try a different keyword.
//                   </p>

//                   {/* Action hint */}
//                   <p className="mt-4 text-xs text-gray-400">
//                     Tip: Check spelling or try more general terms
//                   </p>
//                 </div>
//               )}

//               {!suggestedSearchLoading && suggestions.length > 0 && (
//                 <div className="flex justify-between items-center h-full pb-5">
//                   <h1 className="text-black text-[18px] font-semibold">
//                     Product Search By: {debouncedSearchText}
//                   </h1>

//                   <Link
//                     href={routes.search + replaceSlash(debouncedSearchText)}
//                     onClick={() => setDropdownActive(false)}
//                     className="ml-2 text-[#f26e21] underline hover:no-underline font-medium"
//                   >
//                     View all
//                   </Link>
//                 </div>
//               )}

//               {/* Result state */}
//               {!suggestedSearchLoading && suggestions.length > 0 && (
//                 <div
//                   style={{
//                     display: "grid",
//                     gridTemplateColumns:
//                       "repeat(auto-fill, minmax(180px, 1fr))",
//                     gap: "15px",
//                   }}
//                 >
//                   {suggestions.map((item: any, index: number) => (
//                     <ProductCardSearch
//                       key={index}
//                       productItem={item}
//                       type="sm"
//                       onClose={() => setDropdownActive(false)}
//                       onQuantityChange={() => {}}
//                     />
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

const SearchBar = ({ className, style }: Props) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const classNames = [classes.SearchBar, className || "", classes.lg];

  const [searchText, setSearchText] = useState<string>(
    router.query?.text?.toString() || "",
  );

  // Debounced search value for suggestion API
  const debouncedSearchText = useDebounce(searchText, 800);

  const [dropdownActive, setDropdownActive] = useState<boolean>(false);

  // State to hold trending products
  const [trendingProducts, setTrendingProducts] = useState<any[]>([]);
  const [loadingTrending, setLoadingTrending] = useState(false);

  // Suggested search hook (existing)
  const { suggestedSearch, loading: suggestedSearchLoading } =
    useSuggestedSearch({
      searchText: addSlash(debouncedSearchText),
      flag: dropdownActive && debouncedSearchText.trim().length > 0,
    });

  const suggestions: any[] = suggestedSearch?.suggestion ?? [];

  // Show dropdown whenever search bar is focused
  const shouldShowDropdown = dropdownActive;

  // dropdown active class
  if (shouldShowDropdown) {
    classNames.push(classes.dropdownActive);
  }

  // Fetch trending products when input is focused and searchText is empty
  useEffect(() => {
    if (dropdownActive && searchText.trim() === "") {
      setLoadingTrending(true);
      fetch(
        `${process.env.NEXT_PUBLIC_BASE_API}product/trending-products/storefront/list`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ limit: 12, offset: 0, type: "product" }),
        },
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.Success) {
            setTrendingProducts(data.Data);
          } else {
            setTrendingProducts([]);
          }
        })
        .catch(() => setTrendingProducts([]))
        .finally(() => setLoadingTrending(false));
    }
    // Keep trending products visible while typing
  }, [dropdownActive, searchText]);

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setDropdownActive(false);
    dispatch(setSearchProducts([]));
    dispatch(setSearchQuery(searchText));
    router.push(routes.search + replaceSlash(searchText)).catch(() => {});
  };

  return (
    <div className="relative w-full" style={style}>
      <form
        className={classNames.join(" ")}
        onSubmit={submitHandler}
        style={{ position: "relative", zIndex: 51 }}
      >
        <input
          type="text"
          className={classes.input}
          value={searchText}
          onFocus={() => setDropdownActive(true)}
          onChange={(e) => {
            setDropdownActive(true);
            setSearchText(e.target.value);
            // setDropdownActive(true);
          }}
          placeholder="Search products"
        />

        <button type="submit" className={classes.btn}>
          <SearchOutlineIcon
            className="!stroke-primary"
            width={20}
            height={20}
          />
        </button>
      </form>

      {/* Search dropdown */}
      {shouldShowDropdown && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setDropdownActive(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              backgroundColor: "rgb(0 0 0 / 30%)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }}
          />

          <div
            className={classes.searchDropdown}
            style={{
              position: "absolute",
              top: "100%",
              left: "72%",
              transform: "translateX(-50%)",
              width: "60vw",
              minWidth: "300px",
              backgroundColor: "white",
              zIndex: 50,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              padding: "30px 30px",
              marginTop: "15px",
              borderRadius: "12px",
              maxHeight: "400px",
              overflowY: "auto",
              minHeight: "60vh",
            }}
          >
            <div>
              {/* Loading state - only show when initially loading trending */}
              {suggestedSearchLoading && (
                <div className="flex justify-center items-center h-full">
                  <Loader size={100} />
                </div>
              )}
              {loadingTrending && trendingProducts.length === 0 && (
                <div className="flex justify-center items-center h-full">
                  <Loader size={100} />
                </div>
              )}

              {/* No results - only show when actively searching and got no results */}
              {!suggestedSearchLoading &&
                debouncedSearchText.trim().length > 0 &&
                suggestions.length === 0 && (
                  <div className="flex flex-col justify-center items-center h-full py-16 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                      <SearchOutlineIcon
                        width={28}
                        height={28}
                        className="text-gray-400"
                      />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      No products found
                    </h2>
                    <p className="mt-2 max-w-xs text-sm text-gray-500">
                      We couldn’t find any products matching
                      <span className="font-medium text-gray-700">
                        {" "}
                        “{debouncedSearchText || searchText}”
                      </span>
                      . Try a different keyword.
                    </p>
                    <p className="mt-4 text-xs text-gray-400">
                      Tip: Check spelling or try more general terms
                    </p>
                  </div>
                )}

              {/* Show trending products as fallback when no search results */}
              {suggestions.length === 0 &&
                trendingProducts.length > 0 &&
                debouncedSearchText.trim().length === 0 && (
                  <>
                    <h1 className="text-black text-[18px] font-semibold mb-5">
                      Most Searched Products
                    </h1>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      {trendingProducts.map((item, index) => (
                        <ProductCardSearch
                          key={index + "-trending-"+item.p_id}
                          productItem={item}
                          type="sm"
                          onClose={() => setDropdownActive(false)}
                          onQuantityChange={() => {}}
                        />
                      ))}
                    </div>
                  </>
                )}

              {/* Show suggestions when user typed */}
              {!suggestedSearchLoading &&
                debouncedSearchText.trim().length > 0 &&
                suggestions.length > 0 && (
                  <>
                    <div className="flex justify-between items-center h-full pb-5">
                      <h1 className="text-black text-[18px] font-semibold">
                        Product Search By: {debouncedSearchText}
                      </h1>

                      <Link
                        href={routes.search + replaceSlash(debouncedSearchText)}
                        onClick={() => setDropdownActive(false)}
                        className="ml-2 text-[#f26e21] underline hover:no-underline font-medium"
                      >
                        View all
                      </Link>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      {suggestions.map((item: any, index: number) => (
                        <ProductCardSearch
                          key={index + "-suggestion-" + item.p_id}
                          productItem={item}
                          type="sm"
                          onClose={() => setDropdownActive(false)}
                          onQuantityChange={() => {}}
                        />
                      ))}
                    </div>
                  </>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const SearchBarMobile = ({ className, style, onClick }: Props) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const classNames = [classes.SearchBar, className || "", classes.lg];

  const [searchText, setSearchText] = useState<string>(
    router.query?.text?.toString() || "",
  );

  // Debounced search value for suggestion API
  const debouncedSearchText = useDebounce(searchText, 800);

  const [dropdownActive, setDropdownActive] = useState<boolean>(false);

  // State to hold trending products
  const [trendingProducts, setTrendingProducts] = useState<any[]>([]);
  const [loadingTrending, setLoadingTrending] = useState(false);

  // Suggested search hook (existing)
  const { suggestedSearch, loading: suggestedSearchLoading } =
    useSuggestedSearch({
      searchText: addSlash(debouncedSearchText),
      flag: dropdownActive && debouncedSearchText.trim().length > 0,
    });

  const suggestions: any[] = suggestedSearch?.suggestion ?? [];

  // Show dropdown whenever search bar is focused
  const shouldShowDropdown = dropdownActive;

  // dropdown active class
  if (shouldShowDropdown) {
    classNames.push(classes.dropdownActive);
  }

  // Fetch trending products when input is focused and searchText is empty
  useEffect(() => {
    if (dropdownActive && searchText.trim() === "") {
      setLoadingTrending(true);
      fetch(
        `${process.env.NEXT_PUBLIC_BASE_API}product/trending-products/storefront/list`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ limit: 12, offset: 0, type: "product" }),
        },
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.Success) {
            setTrendingProducts(data.Data);
          } else {
            setTrendingProducts([]);
          }
        })
        .catch(() => setTrendingProducts([]))
        .finally(() => setLoadingTrending(false));
    }
    // Keep trending products visible while typing
  }, [dropdownActive, searchText]);

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setDropdownActive(false);
    dispatch(setSearchProducts([]));
    dispatch(setSearchQuery(searchText));
    router.push(routes.search + replaceSlash(searchText)).catch(() => {});
  };

  return (
    <div className="relative w-full" style={style}>

      <form
        className={classNames.join(" ")}
        onSubmit={submitHandler}
        onClick={() => {
          setDropdownActive(true);
        }}
        style={{ position: "relative", zIndex: 51 }}
      >
        <input
          type="text"
          className={`${classes.input} !w-8 !px-2 !py-1`}
          value={searchText}
          onFocus={() => setDropdownActive(true)}
          onChange={(e) => {
            setDropdownActive(true);
            setSearchText(e.target.value);
            // setDropdownActive(true);
          }}
          placeholder="Search products"
        />
        <button type="submit" className={classes.btn}>
          <SearchOutlineIcon
            className="!stroke-primary"
            width={16}
            height={16}
          />
        </button>
      </form>

      {/* Search dropdown */}
      {shouldShowDropdown && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setDropdownActive(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              backgroundColor: "rgb(0 0 0 / 30%)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }}
          />

          <div
            className={classes.searchDropdown}
            style={{
              position: "absolute",
              top: "100%",
              left: "0%",
              transform: "translateX(-50%)",
              width: "85vw",
              minWidth: "300px",
              backgroundColor: "white",
              zIndex: 50,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              padding: "15px",
              marginTop: "15px",
              borderRadius: "12px",
              maxHeight: "400px",
              overflowY: "auto",
              minHeight: "60vh",
            }}
          >
            <div>
              {/* Loading state - only show when initially loading trending */}
              {suggestedSearchLoading && (
                <div className="flex justify-center items-center h-full">
                  <Loader size={40} />
                </div>
              )}
              {loadingTrending && trendingProducts.length === 0 && (
                <div className="flex justify-center items-center h-full">
                  <Loader size={40} />
                </div>
              )}

              {/* No results - only show when actively searching and got no results */}
              {!suggestedSearchLoading &&
                debouncedSearchText.trim().length > 0 &&
                suggestions.length === 0 && (
                  <div className="flex flex-col justify-center items-center h-full py-16 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                      <SearchOutlineIcon
                        width={28}
                        height={28}
                        className="text-gray-400"
                      />
                    </div>
                    <h2 className="text-sm font-semibold text-gray-800">
                      No products found
                    </h2>
                    <p className="mt-2 max-w-xs text-xs text-gray-500">
                      We couldn’t find any products matching
                      <span className="font-medium text-gray-700 ">
                        {" "}
                        “{debouncedSearchText || searchText}”
                      </span>
                      . Try a different keyword.
                    </p>
                    <p className="mt-4 text-xs text-gray-400">
                      Tip: Check spelling or try more general terms
                    </p>
                  </div>
                )}

              {/* Show trending products as fallback when no search results */}
              {suggestions.length === 0 &&
                trendingProducts.length > 0 &&
                debouncedSearchText.trim().length === 0 && (
                  <>
                    <h1 className="text-black text-[14px] font-semibold mb-5">
                      Most Searched Products
                    </h1>
                    <div
                      className="grid"
                      style={{
                        gridTemplateColumns: "repeat(2,1fr)",
                        gap: "12px",
                      }}
                    >
                      {trendingProducts.map((item, index) => (
                        <ProductCardSearch
                          key={index}
                          productItem={item}
                          type="sm"
                          onClose={() => setDropdownActive(false)}
                          onQuantityChange={() => {}}
                        />
                      ))}
                    </div>
                  </>
                )}

              {/* Show suggestions when user typed */}
              {!suggestedSearchLoading &&
                debouncedSearchText.trim().length > 0 &&
                suggestions.length > 0 && (
                  <>
                    <div className="flex justify-between items-center h-full pb-5">
                      <h1 className="text-black text-[14px] font-semibold">
                        Product Search By: {debouncedSearchText}
                      </h1>

                      <Link
                        href={routes.search + replaceSlash(debouncedSearchText)}
                        onClick={() => setDropdownActive(false)}
                        className="ml-2 text-[#f26e21] underline hover:no-underline font-medium text-[12px]"
                      >
                        View all
                      </Link>
                    </div>

                    <div
                      className="grid"
                      style={{
                        gridTemplateColumns: "repeat(2,1fr)",
                        gap: "12px",
                      }}
                    >
                      {suggestions.map((item: any, index: number) => (
                        <ProductCardSearch
                          key={index}
                          productItem={item}
                          type="sm"
                          onClose={() => setDropdownActive(false)}
                          onQuantityChange={() => {}}
                        />
                      ))}
                    </div>
                  </>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
// export const SearchBarMobile = ({ className, style, onClick }: Props) => {
//   const classNames = [classes.SearchBar, classes.mobile, className];
//   const dispatch = useAppDispatch();

//   const router = useRouter();
//   const [searchText, setSearchText] = useState("");

//   const [dropdownActive, setDropdownActive] = useState(false);

//   const { suggestedSearch, loading: suggestedSearchLoading } =
//     useSuggestedSearch({
//       searchText,
//       flag: true && dropdownActive,
//       limit: 30,
//     });

//   useEffect(() => {
//     if (!router.pathname?.includes(routes.search)) {
//       setSearchText("");
//       setDropdownActive(false);
//     }
//   }, [router.pathname]);

//   const submitHandler = (e: React.FormEvent) => {
//     e.preventDefault();
//     dispatch(setSearchQuery(searchText));
//     router.push(routes.search + replaceSlash(searchText)).catch((error) => {
//       // Handle route change errors
//       if (error?.cancelled) {
//         return;
//       }
//       console.error("Search route change failed:", error);
//     });

//     setDropdownActive(false);
//   };

//   return (
//     <>
//       <form
//         className={classNames.join(" ")}
//         onSubmit={submitHandler}
//         onClick={() => {
//           setDropdownActive(true);
//         }}
//       >
//         <input
//           type="text"
//           className={`${classes.input} !w-10`}
//           value={searchText}
//           onChange={(e) => {
//             setSearchText(e.target.value);
//             // dispatch(setSearchQuery(e.target.value));
//             if (e.target.value) {
//               routeChangeDebounce(
//                 router,
//                 routes.search + replaceSlash(e.target.value),
//               );
//             }
//           }}
//           placeholder="Search products"
//         />

//         <button type="submit" className={classes.button}>
//           <SearchOutlineIcon className={classes.icon} width={16} height={16} />
//         </button>
//       </form>
//       {dropdownActive && !!suggestedSearch?.suggestion?.length && (
//         <div className={classes.SearchMobileBottom}>
//           <div className={classes.contents + " scrollbar-ui"}>
//             <div
//               className={classes.overlay}
//               onClick={() => {
//                 setDropdownActive(false);
//               }}
//             ></div>

//             <button
//               className={classes.closeButton}
//               onClick={() => {
//                 setDropdownActive(false);
//               }}
//             >
//               <CloseIcon size="16px" />
//             </button>

//             <SuggestedSearch
//               size="sm"
//               suggestedSearch={suggestedSearch}
//               loading={suggestedSearchLoading}
//               onSelect={(item, newTab) => {
//                 router.push(routes.product + item.slug).catch((error) => {
//                   // Handle route change errors
//                   if (error?.cancelled) {
//                     return;
//                   }
//                   console.error("Product route change failed:", error);
//                 });
//                 setDropdownActive(false);
//               }}
//             />
//           </div>
//         </div>
//       )}
//     </>
//   );
// };
export const SearchBarMobileOld = ({ className, style, onClick }: Props) => {
  const classNames = [classes.SearchBar, classes.mobile, className];

  return (
    <div className={classNames.join(" ")} onClick={onClick}>
      {/* <input
				type="text"
				className={classes.input}
				value={searchText}
				onChange={(e) => {
					setSearchText(e.target.value);
				}}
				placeholder="Search products"
			/> */}

      <button type="button" className={classes.button}>
        <SearchOutlineIcon className={classes.icon} width={20} height={20} />
      </button>
    </div>
  );
};

export const SearchFullUI = ({ onBack }: { onBack?: () => void }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [searchText, setSearchText] = useState("");
  const [closing, setClosing] = useState(false);

  const { suggestedSearch, loading: suggestedSearchLoading } =
    useSuggestedSearch({ searchText, flag: true, limit: 30 });

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    router.push(routes.search + replaceSlash(searchText)).catch((error) => {
      // Handle route change errors
      if (error?.cancelled) {
        return;
      }
      console.error("Search route change failed:", error);
    });

    handlerBackButton();
  };

  const handlerBackButton = () => {
    setClosing(true);

    setTimeout(() => {
      if (onBack) {
        onBack();
        dispatch(setSearchProducts([]));
      }
    }, 300);
  };

  return (
    <div className={classes.SearchFullUI + " " + (closing && classes.closing)}>
      <div className={classes.top}>
        <div className="container">
          <div className="flex justify-between items-center">
            <button className={classes.backButton} onClick={handlerBackButton}>
              <ArrowSquareLeftOutlineIcon className={classes.icon} />
            </button>

            <form className={classes.SearchBar} onSubmit={submitHandler}>
              <input
                type="text"
                className={classes.input}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                placeholder="Search products"
              />

              <button type="submit" className={classes.btn}>
                Search
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className={classes.bottom}>
        <SuggestedSearch
          suggestedSearch={suggestedSearch}
          loading={suggestedSearchLoading}
          onSelect={(item, newTab) => {
            router.push(routes.product + item.slug).catch((error) => {
              // Handle route change errors
              if (error?.cancelled) {
                return;
              }
              console.error("Product route change failed:", error);
            });
            handlerBackButton();
          }}
        />
      </div>
    </div>
  );
};

export default SearchBar;
